import { Injectable, effect, inject, signal } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import {
  ActivatedRouteSnapshot,
  NavigationEnd,
  Router,
} from '@angular/router';
import { filter } from 'rxjs';

import { Translations } from '../types/i18n.types';
import { I18nService } from './i18n.service';

export interface SeoBlock {
  title: string;
  description: string;
}

/* Reads the current route's `data.seoKey` on every NavigationEnd,
   looks up `translations()[seoKey].seo`, and applies <title>, meta
   description, and Open Graph tags. Reacts to both route changes
   and locale changes.

   For prerendered routes, this runs synchronously during SSR (the
   initial navigation fires NavigationEnd inside the render pass),
   so the emitted HTML carries the correct <title> and <meta> from
   the start — good for crawlers, unfurlers and AI answer engines. */
@Injectable({ providedIn: 'root' })
export class SeoService {
  private readonly title = inject(Title);
  private readonly meta = inject(Meta);
  private readonly router = inject(Router);
  private readonly i18n = inject(I18nService);

  private readonly seoKey = signal<string | null>(null);

  constructor() {
    this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe(() => {
        this.seoKey.set(this.findSeoKey(this.router.routerState.snapshot.root));
      });

    effect(() => {
      const t = this.i18n.translations();
      const key = this.seoKey();
      if (!t || !key) return;
      const block = (t as unknown as Record<string, { seo?: SeoBlock }>)[key];
      if (block?.seo) this.apply(block.seo);
    });
  }

  private findSeoKey(snapshot: ActivatedRouteSnapshot): string | null {
    const key = snapshot.data['seoKey'] as string | undefined;
    if (key) return key;
    for (const child of snapshot.children) {
      const nested = this.findSeoKey(child);
      if (nested) return nested;
    }
    return null;
  }

  private apply(seo: SeoBlock): void {
    this.title.setTitle(seo.title);
    this.meta.updateTag({ name: 'description', content: seo.description });
    this.meta.updateTag({ property: 'og:title', content: seo.title });
    this.meta.updateTag({ property: 'og:description', content: seo.description });
    this.meta.updateTag({ property: 'og:type', content: 'website' });
  }
}

/* Compile-time proof that SeoBlock is compatible with any Translations
   sub-key we wire in app.routes.ts. Never called at runtime. */
type _SeoKeyOf<T> = { [K in keyof T]: T[K] extends { seo: SeoBlock } ? K : never }[keyof T];
export type SeoKey = _SeoKeyOf<Translations>;
